import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit,
  Delete,
  Send,
  Search,
  Visibility,
  FilterList,
  ArrowBack,
} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QuotationEdit = ({ onBack, onEdit }) => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', subject: '', message: '' });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Fetch draft quotations
  useEffect(() => {
    fetchDraftQuotations();
  }, []);

  const fetchDraftQuotations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/quotations?status=draft&limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setQuotations(data.data.quotations);
      } else {
        setError(data.message || 'Failed to fetch quotations');
      }
    } catch (err) {
      setError('Failed to fetch quotations: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit - Navigate to generate page with quotation data
  const handleEdit = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/quotations/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Store the quotation data in sessionStorage
        sessionStorage.setItem('editQuotation', JSON.stringify(data.data.quotation));
        // Call onEdit callback to navigate within dashboard
        if (onEdit) {
          onEdit();
        }
      } else {
        setError('Failed to fetch quotation details');
      }
    } catch (err) {
      setError('Failed to load quotation for editing');
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!selectedQuotation) return;

    try {
      const response = await fetch(`${API_URL}/api/quotations/${selectedQuotation.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setQuotations(prev => prev.filter(q => q.id !== selectedQuotation.id));
        setDeleteDialogOpen(false);
        setSelectedQuotation(null);
      } else {
        setError(data.message || 'Failed to delete quotation');
      }
    } catch (err) {
      setError('Failed to delete quotation');
    }
  };

  // Handle Send Email
  const handleSendEmail = async () => {
    if (!selectedQuotation) return;

    try {
      const response = await fetch(`${API_URL}/api/quotations/${selectedQuotation.id}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(emailData),
      });

      const data = await response.json();

      if (data.success) {
        alert('Quotation sent successfully!');
        setEmailDialogOpen(false);
        setSelectedQuotation(null);
        setEmailData({ email: '', subject: '', message: '' });
        fetchDraftQuotations(); // Refresh list
      } else {
        setError(data.message || 'Failed to send quotation');
      }
    } catch (err) {
      setError('Failed to send quotation');
    }
  };

  // Handle View Details
  const handleView = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/quotations/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setSelectedQuotation(data.data.quotation);
        setViewDialogOpen(true);
      }
    } catch (err) {
      setError('Failed to fetch quotation details');
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  // Filter quotations
  const filteredQuotations = quotations.filter(q => 
    q.quotation_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_info.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">
            Draft Quotations
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search quotations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={fetchDraftQuotations}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      
      

      {/* Quotations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Quotation</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Valid Till</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuotations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No draft quotations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredQuotations.map((quotation) => (
                <TableRow key={quotation.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {quotation.quotation_number}
                    </Typography>
                  </TableCell>
                  <TableCell>{quotation.client_info.name}</TableCell>
                  <TableCell>{new Date(quotation.date).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(quotation.valid_till).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(quotation.grand_total, quotation.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={quotation.status} 
                      color="warning"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(quotation.id)}
                        title="View"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleEdit(quotation.id)}
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => {
                          setSelectedQuotation(quotation);
                          setEmailData({
                            email: quotation.client_info.email || '',
                            subject: `Quotation ${quotation.quotation_number}`,
                            message: `Please find attached quotation ${quotation.quotation_number}`,
                          });
                          setEmailDialogOpen(true);
                        }}
                        title="Send Email"
                      >
                        <Send />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedQuotation(quotation);
                          setDeleteDialogOpen(true);
                        }}
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete quotation{' '}
            <strong>{selectedQuotation?.quotation_number}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Quotation via Email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Recipient Email"
              type="email"
              value={emailData.email}
              onChange={(e) => setEmailData(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Subject"
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Message"
              multiline
              rows={4}
              value={emailData.message}
              onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            startIcon={<Send />}
            disabled={!emailData.email}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Quotation Details</DialogTitle>
        <DialogContent>
          {selectedQuotation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Quotation Number</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedQuotation.quotation_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip label={selectedQuotation.status} color="warning" size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Client Name</Typography>
                  <Typography variant="body1">{selectedQuotation.client_info.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Grand Total</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(selectedQuotation.grand_total, selectedQuotation.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                  <Typography variant="body1">{new Date(selectedQuotation.date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Valid Till</Typography>
                  <Typography variant="body1">{new Date(selectedQuotation.valid_till).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Scope of Work</Typography>
                  <Typography variant="body2">{selectedQuotation.scope_of_work}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotationEdit;